import { useState, type ReactNode } from "react";
import { Package, Plus, X, Check } from "lucide-react";

// Mirrors the Packages tab in iviz-ui (components/iviz/PackagesPanel.tsx): pre-installed list,
// name + optional version add, "Added by you", Save & Install. Versions come from the kernel manifest.
const PREINSTALLED = [["pandas", "2.3.3"], ["numpy", "2.5.2"], ["scikit-learn", "1.9.0"], ["statsmodels", "0.14.6"], ["matplotlib", "3.11.1"]];

type Spec = { name: string; version: string };

const COLUMN_LABEL = "text-[10px] font-bold uppercase tracking-[0.08em] text-[#5C5C66] dark:text-[#9A9A9A] mb-2";
const ROW = "flex items-center justify-between gap-2 rounded-lg border border-[#0A0A0F]/10 dark:border-white/10 px-3 py-2";
const INPUT = "min-w-0 rounded-lg border border-[#0A0A0F]/15 dark:border-white/15 bg-transparent px-3 py-2 text-xs text-[#0A0A0F] dark:text-white placeholder:text-[#5C5C66] dark:placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#1E4FD8] dark:focus:border-[#4A90D9]";

function PackageRow({ name, version, trailing }: { name: string; version?: string; trailing: ReactNode }) {
  return (
    <div className={ROW}>
      <span className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-medium text-[#0A0A0F] dark:text-white truncate">{name}</span>
        {version && <span className="text-[11px] text-[#5C5C66] dark:text-[#9A9A9A]">{version}</span>}
      </span>
      {trailing}
    </div>
  );
}

function AddPackageForm({ onAdd }: { onAdd: (spec: Spec) => void }) {
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const submit = () => { if (!name.trim()) return; onAdd({ name: name.trim(), version: version.trim() }); setName(""); setVersion(""); };
  return (
    <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Package name (e.g. pyvis)" aria-label="Package name" className={`${INPUT} flex-[2]`} />
      <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version (optional)" aria-label="Version" className={`${INPUT} flex-1`} />
      <button type="submit" aria-label="Add package" disabled={!name.trim()} className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center bg-[#1E4FD8] dark:bg-[#4A90D9] text-white dark:text-[#050505] disabled:opacity-40">
        <Plus size={15} />
      </button>
    </form>
  );
}

function PanelColumns({ added, onAdd, onRemove }: { added: Spec[]; onAdd: (s: Spec) => void; onRemove: (name: string) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <p className={COLUMN_LABEL}>Add package</p>
        <AddPackageForm onAdd={onAdd} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="min-w-0">
          <p className={COLUMN_LABEL}>Pre-installed (Python)</p>
          <div className="space-y-1.5">
            {PREINSTALLED.map(([n, v]) => <PackageRow key={n} name={n} version={v} trailing={<span className="text-[9px] font-semibold uppercase tracking-wide text-[#5C5C66] dark:text-[#9A9A9A] rounded border border-[#0A0A0F]/10 dark:border-white/10 px-1.5 py-0.5">default</span>} />)}
          </div>
        </div>
        <div className="min-w-0">
          <p className={COLUMN_LABEL}>Added by you</p>
          <div className="space-y-1.5">
            {added.length === 0 && <p className="text-xs text-[#5C5C66] dark:text-[#9A9A9A] py-2">No packages added yet.</p>}
            {added.map((s) => (
              <PackageRow key={s.name} name={s.name} version={s.version} trailing={
                <button onClick={() => onRemove(s.name)} aria-label={`Remove ${s.name}`} className="text-[#5C5C66] dark:text-[#9A9A9A] hover:text-[#0A0A0F] dark:hover:text-white"><X size={14} /></button>
              } />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PanelWindow() {
  const [added, setAdded] = useState<Spec[]>([{ name: "pyvis", version: "0.3.2" }]);
  const [state, setState] = useState<"idle" | "installing" | "done">("idle");
  const add = (s: Spec) => { setAdded((prev) => (prev.some((p) => p.name.toLowerCase() === s.name.toLowerCase()) ? prev : [...prev, s])); setState("idle"); };
  const install = () => { setState("installing"); setTimeout(() => setState("done"), 1200); };
  return (
    <div className="rounded-xl border border-[#0A0A0F]/10 dark:border-white/10 bg-[#0A0A0F]/[0.02] dark:bg-black/30 p-5">
      <p className="text-sm font-medium text-[#0A0A0F] dark:text-white">Packages</p>
      <p className="text-[11px] text-[#5C5C66] dark:text-[#9A9A9A] mb-4">Installed into this notebook&apos;s kernel. Every fresh kernel gets them automatically.</p>
      <PanelColumns added={added} onAdd={add} onRemove={(n) => { setAdded((prev) => prev.filter((p) => p.name !== n)); setState("idle"); }} />
      <div className="mt-4 flex justify-end">
        <button onClick={install} disabled={state === "installing"} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E4FD8] dark:bg-[#4A90D9] px-4 py-2 text-xs font-semibold text-white dark:text-[#050505] disabled:opacity-60">
          {state === "installing" ? "Installing…" : state === "done" ? <><Check size={13} /> Installed</> : "Save & Install"}
        </button>
      </div>
    </div>
  );
}

export function PackagesPanelMock() {
  return (
    <div className="mt-6 rounded-2xl border border-[#1E4FD8]/30 dark:border-[#4A90D9]/30 bg-white dark:bg-[#0D0D10] p-6 md:p-8 grid lg:grid-cols-[1fr_1.5fr] gap-8 items-center">
      <div>
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1E4FD8] dark:text-[#4A90D9] mb-3"><Package size={15} /> Packages tab</p>
        <p className="text-xl font-normal text-[#0A0A0F] dark:text-white mb-3">Add any package. No commands needed.</p>
        <p className="text-sm leading-[1.7] text-[#5C5C66] dark:text-[#9A9A9A]">
          Open the Packages tab in any notebook, type a package name and, if you like, a version,
          then click Save &amp; Install. iViz checks the name against PyPI or CRAN, installs it into
          your running kernel straight away, and every fresh kernel gets it automatically. Merlin can
          request installs for you, too.
        </p>
        <p className="mt-4 text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Try it: add a package in the panel.</p>
      </div>
      <PanelWindow />
    </div>
  );
}
